var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { extend, Property, ChildProperty, Complex, Collection } from '@syncfusion/ej2-base';
import { CellStyle, wrapEvent, Chart } from '../common/index';
import { Image, updateCell } from '../common/index';
import { getRow } from './index';
import { getSheet } from './sheet';
/**
 * Represents the cell.
 */
export class Cell extends ChildProperty {
}
__decorate([
    Property('')
], Cell.prototype, "notes", void 0);
__decorate([
    Collection([], Image)
], Cell.prototype, "image", void 0);
__decorate([
    Collection([], Chart)
], Cell.prototype, "chart", void 0);
__decorate([
    Property('')
], Cell.prototype, "value", void 0);
__decorate([
    Property('')
], Cell.prototype, "formula", void 0);
__decorate([
    Property(0)
], Cell.prototype, "index", void 0);
__decorate([
    Property('General')
], Cell.prototype, "format", void 0);
__decorate([
    Complex({}, CellStyle)
], Cell.prototype, "style", void 0);
__decorate([
    Property('')
], Cell.prototype, "hyperlink", void 0);
__decorate([
    Property(false)
], Cell.prototype, "wrap", void 0);
__decorate([
    Property(true)
], Cell.prototype, "isLocked", void 0);
__decorate([
    Property('')
], Cell.prototype, "validation", void 0);
__decorate([
    Property(1)
], Cell.prototype, "colSpan", void 0);
__decorate([
    Property(1)
], Cell.prototype, "rowSpan", void 0);
__decorate([
    Property(false)
], Cell.prototype, "isReadOnly", void 0);
/**
 * @hidden
 * @param {number} rowIndex - Specifies the rowIndex.
 * @param {number} colIndex - Specifies the colIndex.
 * @param {SheetModel} sheet - Specifies the sheet.
 * @param {boolean} isInitRow - Specifies the isInitRow.
 * @param {boolean} returnEmptyObjIfNull - Specifies the bool value.
 * @returns {CellModel} - get the cell.
 */
export function getCell(rowIndex, colIndex, sheet, isInitRow, returnEmptyObjIfNull) {
    const row = getRow(sheet, rowIndex);
    if (!row || !row.cells) {
        if (isInitRow) {
            if (!row) {
                sheet.rows[rowIndex] = { cells: [] };
            }
            else {
                sheet.rows[rowIndex].cells = [];
            }
        }
        else {
            return returnEmptyObjIfNull ? {} : null;
        }
    }
    return sheet.rows[rowIndex].cells[colIndex] || (returnEmptyObjIfNull ? {} : null);
}
/**
 * @hidden
 * @param {number} rowIndex - Specifies the rowIndex.
 * @param {number} colIndex - Specifies the colIndex.
 * @param {SheetModel} sheet - Specifies the sheet.
 * @param {CellModel} cell - Specifies the cell.
 * @param {boolean} isExtend - Specifies the bool value.
 * @returns {void} - set the cell.
 */
export function setCell(rowIndex, colIndex, sheet, cell, isExtend) {
    if (!sheet.rows[rowIndex]) {
        sheet.rows[rowIndex] = { cells: [] };
    }
    else if (!sheet.rows[rowIndex].cells) {
        sheet.rows[rowIndex].cells = [];
    }
    if (isExtend && sheet.rows[rowIndex].cells[colIndex]) {
        extend(sheet.rows[rowIndex].cells[colIndex], cell, null, true);
    }
    else {
        sheet.rows[rowIndex].cells[colIndex] = cell;
    }
}
/**
 * @hidden
 * @param {CellStyleModel} style - Specifies the style.
 * @param {boolean} defaultKey - Specifies the defaultKey.
 * @returns {CellStyleModel} - Specifies the CellStyleModel.
 */
export function skipDefaultValue(style, defaultKey) {
    const defaultProps = { fontFamily: 'Calibri', verticalAlign: 'bottom', textIndent: '0pt', backgroundColor: '#ffffff',
        color: '#000000', textAlign: 'left', fontSize: '11pt', fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none',
        border: '', borderLeft: '', borderTop: '', borderRight: '', borderBottom: '' };
    const changedProps = {};
    let styleVal;
    Object.keys(defaultKey ? defaultProps : style).forEach((propName) => {
        styleVal = style[`${propName}`];
        if (styleVal !== defaultProps[`${propName}`]) {
            changedProps[`${propName}`] = styleVal;
        }
    });
    return changedProps;
}
/**
 * @hidden
 * @param {string} address - Specifies the address.
 * @param {boolean} wrap - Specifies the wrap.
 * @param {Workbook} context - Specifies the context.
 * @param {Workbook} preventEvt - Preventing the before cell update event.
 * @param {boolean} isPublic - Specifies if the wrap operation is invoked from a public method.
 * @returns {void} - Specifies the wrap.
 */
export function wrap(address, wrap = true, context, preventEvt, isPublic) {
    if (context.allowWrap) {
        const addressInfo = context.getAddressInfo(address);
        const rng = addressInfo.indices;
        const sheet = getSheet(context, addressInfo.sheetIndex);
        const uiRefresh = addressInfo.sheetIndex === context.activeSheetIndex;
        let cancel = !preventEvt;
        for (let i = rng[0]; i <= rng[2]; i++) {
            for (let j = rng[1]; j <= rng[3]; j++) {
                cancel = updateCell(context, sheet, { cell: { wrap: wrap }, rowIdx: i, colIdx: j, preventEvt: preventEvt });
                if (!cancel && uiRefresh) {
                    context.notify(wrapEvent, { range: [i, j, i, j], wrap: wrap, sheet: sheet, initial: true, isPublic: isPublic });
                }
            }
        }
        context.setProperties({ sheets: context.sheets }, true);
    }
}
/**
 * @hidden
 * @param {string} format - Specifies the cell format.
 * @returns {string} - Specifies the supported color code.
 */
export function getColorCode(format) {
    let code;
    if (format.indexOf('[') > -1) {
        const colorValue = format.split('[')[1].split(']')[0];
        const customColors = getCustomColors();
        if (customColors.indexOf(colorValue) > -1) {
            code = colorValue;
        }
    }
    return code;
}
/**
 * @hidden
 * @returns {string[]} - Returns the custom format colors
 */
export function getCustomColors() {
    return ['Black', 'Blue', 'Cyan', 'Green', 'Magenta', 'Red', 'White', 'Yellow'];
}
/**
 * @param {string} format - Specify the format.
 * @param {boolean} checkTime - Specify the checktime.
 * @param {Object} option - Specify rhe option value.
 * @param {string} option.type - Specify the type.
 * @param {boolean} checkBoth - Specify check both values.
 * @returns {boolean} - This function is used to return is custom Data time or not.
 * @hidden
 */
export function isCustomDateTime(format, checkTime, option, checkBoth) {
    let isCustom;
    if (format.includes('"')) {
        const formatSection = format.split(';');
        let endStrIdx;
        let prevChar;
        formatSection.forEach((formatCode, index) => {
            for (let idx = 0; idx < formatCode.length; idx++) {
                if (formatCode[idx] === '"' && formatCode[idx - 1] !== '\\') {
                    endStrIdx = idx;
                    do {
                        endStrIdx = formatCode.indexOf('"', endStrIdx + 1);
                        prevChar = formatCode[endStrIdx - 1];
                    } while (prevChar === '\\' || prevChar === '_' || prevChar === '*');
                    if (endStrIdx === -1) {
                        break;
                    }
                    formatCode = formatCode.substring(0, idx) + formatCode.substring(endStrIdx + 1);
                    idx--;
                }
            }
            formatSection[index] = formatCode;
        });
        format = formatSection.join(';');
    }
    if ((format.includes('d') || format.includes('y')) && !format.includes('#') && !getColorCode(format)) {
        if (option) {
            option.type = 'date';
        }
        if (checkBoth && format.includes(' ') && format.split(' ').length === 2) {
            format = format.split(' ')[1];
        }
        else {
            checkTime = false;
        }
        isCustom = true;
    }
    if (checkTime && (format.includes('h') || format.includes('m') || format.includes('s')) && !format.includes('#') &&
        !getColorCode(format)) {
        if (option) {
            option.type = option.type || '';
            option.type += 'time';
        }
        isCustom = true;
    }
    return isCustom;
}
/**
 * Represents the configuration options for the Spreadsheet when opening a document.
 */
export class OpenSettings extends ChildProperty {
}
__decorate([
    Property(0)
], OpenSettings.prototype, "chunkSize", void 0);
__decorate([
    Property(3)
], OpenSettings.prototype, "retryCount", void 0);
__decorate([
    Property(500)
], OpenSettings.prototype, "retryAfterDelay", void 0);
